import React, { ReactElement } from "react";
import { CoObjectItem } from "../../interfaces/CoObject";
import HeaderPanel from "../../layout/HeaderPanel";
import { formatMessage, formatMessageWithParams } from "../../../utils/helpers/LanguageHelper";
import { CommandBarButton, ImageFit } from "@fluentui/react";
import FooterPanel from "../../layout/FooterPanel";
import { selectContentControl } from "../../../office/ContentControl";
import { OperationCompletedPanel } from "../../layout/OperationCompletedPanel";
import { isNotAuthorizedUser } from "../../../utils/user-function/userFunctionUtils";
import { UserFunctionEnum } from "../../../api/models/user-function-enum";
import { ObjectList } from "./ObjectList";
import { useObjectsListUpdatePanel } from "./ObjectsListUpdatePanelHook";
import { NotCompatibleWithWeb } from "../../layout/ConfirmationModal";
import { TrackedChangesModal } from "../TrackedChangesModal";
import { ElaborationWrapper } from "../ElaborationWrapper";
import { OBJECTS_LIST_NOTIFICATION } from "../../../utils/constants/notification";
import { DocumentPart } from "../../../api/models";
import { DocumentPartMetadata } from "../../../api/models/document-part-metadata";
import { CustomModal } from "../../layout/ActionModal";
import { deleteObjectsAsync } from "../../../redux/feature/cpm/cpm-slice";
import { invalidate } from "../../../redux/feature/validation/validation-slice";
import { isFeatureActiveOnCurrentClient } from "../../../api/config/ExternalConfig";
import { useAppDispatch } from "../../../redux/hooks/hooks";

export interface ObjectsListProps {
  documentCoObjects: CoObjectItem[];
  documentPart: DocumentPart;
  documentPartMetadata: DocumentPartMetadata;
  reloadCoObjects: () => void;
  footerComponents: (props: {
    showUpdateButton?: boolean;
    showDeleteButton?: boolean;
    showLockButton?: boolean | "lock" | "unlock" | "disabled-lock";
    selectedObjects: Map<string, CoObjectItem>;
    username: string;
    documentPart: DocumentPart;
    documentPartMetadata: DocumentPartMetadata;
    onDelete?: () => void;
    onToggleLock?: () => void;
    isNotAuthorized: boolean;
  }) => ReactElement;
  page: "objectsList" | "updates";
}

export const iconStyles = {
  root: {
    fontWeight: "bold",
  },
};

export const ObjectsListUpdatePanel: React.FunctionComponent<ObjectsListProps> = (props) => {
  const {
    documentCoObjects,
    documentPart: originalDocumentPart,
    documentPartMetadata: originalDocumentPartMetadata,
    reloadCoObjects: originalReloadCoObjects,
    footerComponents,
    page,
  } = props;

  // Mark original props as intentionally unused here (used inside hook logic)
  const _unused = [documentCoObjects, originalDocumentPart, originalDocumentPartMetadata, originalReloadCoObjects];
  if (_unused.length === -1) {
    /* no-op */
  }

  const {
    state,
    getHeaderTitle,
    documentPart,
    documentPartMetadata,
    showBackButton,
    notAuthorizedUser,
    showManageButton,
    showUpdatesButton,
    multiselectable,
    protectionType,
    username,
    user,
    blockingElaboration,
    setShowAvailableUpdates,
    setManageMode,
    showFooter,
    showFooterUpdateButton,
    showFooterDeleteButton,
    showFooterLockButton,
    allowFiltering,
    setTrackChangesObjectItem,
    setSelectedObjects,
    setHighlightedObject,
    setConfirmDelete,
    toggleLockOnSelectedObjects,
    reloadCoObjects,
  } = useObjectsListUpdatePanel(props);

  const dispatch = useAppDispatch();

  const hasObjects = state.listObjects.length > 0;
  const isConfirmDelete = state.confirmDelete;

  const handleBack = () => {
    if (state.showAvailableUpdates) setShowAvailableUpdates(false);
    if (state.manageMode) setManageMode(false);
  };

  const renderHeader = () => (
    <HeaderPanel applyPadding>
      <>
        {showBackButton && (
          <CommandBarButton
            iconProps={{ iconName: "ChevronLeft", styles: iconStyles }}
            onClick={handleBack}
            style={{
              color: notAuthorizedUser ? "rgb(200, 198, 196)" : "var(--color-blue)",
              cursor: notAuthorizedUser ? "default" : "pointer",
            }}
            disabled={notAuthorizedUser}
          />
        )}
        {getHeaderTitle()}
      </>
    </HeaderPanel>
  );

  const renderManageUpdateButtons = () => {
    if (!showManageButton && !showUpdatesButton) return null;
    return (
      <>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "5px",
          }}
        >
          {showManageButton && (
            <CommandBarButton
              id="manage_objects_button"
              iconProps={{
                imageProps: { src: "/assets/tools.svg", imageFit: ImageFit.contain, width: "12px", height: "13px" },
                styles: iconStyles,
              }}
              onClick={() => setManageMode(true)}
              styles={{ flexContainer: { flexDirection: "row-reverse" }, label: { fontWeight: "bold" } }}
              style={{ color: protectionType === 3 ? "var(--color-grey)" : "var(--color-blue)" }}
              text={formatMessage("cof.objectslist.manageobjects")}
              disabled={protectionType === 3}
            />
          )}
          {showUpdatesButton && (
            <div>
              <CommandBarButton
                id="available_updates_button"
                iconProps={{ iconName: "Refresh", styles: iconStyles }}
                onClick={() => setShowAvailableUpdates(true)}
                styles={{ flexContainer: { flexDirection: "row-reverse" }, label: { fontWeight: "bold" } }}
                style={{ color: protectionType === 3 || notAuthorizedUser ? "var(--color-grey)" : "var(--color-blue)" }}
                text={formatMessage("cof.objectslist.availableupdate")}
                disabled={notAuthorizedUser || protectionType === 3}
              />
              <br />
            </div>
          )}
        </div>
        <div style={{ borderBottom: `0.01rem solid var(--color-grey)`, width: "100%", height: "3px" }} />
      </>
    );
  };

  const renderObjectListSection = () => (
    <>
      <TrackedChangesModal
        onClose={() => setTrackChangesObjectItem(undefined)}
        trackChangesObjectItem={state.trackChangesObjectItem}
      />
      <ObjectList
        objects={state.listObjects}
        multiselectable={multiselectable}
        allowFiltering={allowFiltering}
        onItemClick={(item) => {
          void selectContentControl(item);
          setHighlightedObject(item);
        }}
        onSelectionChange={multiselectable ? setSelectedObjects : undefined}
      />
    </>
  );

  const FooterComponents = footerComponents; // uppercase alias for JSX usage

  const renderFooter = () => {
    if (!showFooter) return null;
    const footerBaseProps = {
      selectedObjects: state.listObjects.reduce((map, obj) => {
        if (obj.selected) map.set(obj.object.id, obj.object);
        return map;
      }, new Map<string, CoObjectItem>()),
      showUpdateButton: showFooterUpdateButton,
      showDeleteButton: showFooterDeleteButton,
      showLockButton: showFooterLockButton,
      onDelete: () => setConfirmDelete(),
      username,
      documentPart,
      documentPartMetadata,
      isNotAuthorized: isNotAuthorizedUser(UserFunctionEnum.MENU_UPDATE_PANEL_GET_CHANGES, user.userFunctions),
    } as const;

    const lockProps: Record<string, unknown> = {};
    (lockProps as any)["on" + "ToggleLock"] = () => {
      void toggleLockOnSelectedObjects();
    };

    return (
      <FooterPanel>
        <FooterComponents {...(footerBaseProps as any)} {...(lockProps as any)} />
      </FooterPanel>
    );
  };

  const renderDeleteModal = () => (
    <CustomModal
      modalTitle={formatMessage("cof.objectslist.confirmdeleteshapes.title")}
      messageTitle={formatMessageWithParams("cof.objectslist.confirmdeleteobjects.message", {
        count: state.listObjects.filter((o) => o.selected).length,
      })}
      message={null}
      type="delete"
      onHide={() => {}}
      actions={[
        {
          id: "cancel_button",
          title: formatMessage("cof.base.noconfirmdelete"),
          onClick: () => setConfirmDelete(false),
          variant: "blue",
        },
        {
          id: "confirm_button",
          title: formatMessage("cof.base.confirmdelete"),
          onClick: () => {
            deleteObjectsAsync({
              coObjects: state.listObjects.filter((o) => o.selected).map((o) => o.object),
            })
              .then(() => {
                // Force ValidationPanelHook to reload tags/coObjects so the Tags Panel
                // does not keep rendering the just-deleted object (IDS-6546 follow-up).
                dispatch(invalidate());
              })
              .catch(() => {
                /* deletion errors are already surfaced via NotificationManager in cpm-slice */
              })
              .finally(() => {
                setSelectedObjects([]);
                setConfirmDelete(false);
                reloadCoObjects();
              });
          },
          variant: "red_filled",
        },
      ]}
    />
  );

  if (state.showAvailableUpdates && !isFeatureActiveOnCurrentClient()) return <NotCompatibleWithWeb />;

  const emptyMessage =
    page === "updates"
      ? formatMessage("cof.documentmanagement.updates.message.anyfinddocumentpart")
      : formatMessage("cof.editobject.message.anyfindobject");

  if (isConfirmDelete) {
    return (
      <ElaborationWrapper notificationKey={OBJECTS_LIST_NOTIFICATION} elaborationItem={blockingElaboration}>
        <>
          {renderHeader()}
          <div style={{ padding: "10px" }}>{renderDeleteModal()}</div>
        </>
      </ElaborationWrapper>
    );
  }

  if (!hasObjects) {
    return (
      <ElaborationWrapper notificationKey={OBJECTS_LIST_NOTIFICATION} elaborationItem={blockingElaboration}>
        <>
          {renderHeader()}
          <div style={{ padding: "10px" }}>
            <OperationCompletedPanel message={emptyMessage} alt={emptyMessage} icon="/assets/noObject.svg" />
          </div>
        </>
      </ElaborationWrapper>
    );
  }

  return (
    <ElaborationWrapper notificationKey={OBJECTS_LIST_NOTIFICATION} elaborationItem={blockingElaboration}>
      <>
        {renderHeader()}
        <div style={{ padding: "10px" }}>
          {renderManageUpdateButtons()}
          {renderObjectListSection()}
          {renderFooter()}
        </div>
      </>
    </ElaborationWrapper>
  );
};
